import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Cliente, Requester } from '../services/api';
import { listClientes, listRequesters } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { PersonaModal } from '../components/PersonaModal';
import { PersonasTabla } from '../components/PersonasTabla';
import styles from './Mantenimiento.module.css';

/** Valor del filtro para las cuentas anteriores al 85a, que no tienen cliente. */
const SIN_CLIENTE = '__sin__';

/**
 * USUARIOS: las personas de TODOS los clientes (paso 85a). Hasta el 85a esta
 * pantalla mezclaba al equipo interno y a los clientes en dos pestañas; el
 * equipo vive ahora en Técnicos.
 *
 * El buscador y el filtro recortan en el cliente lo que la API ya devolvió:
 * son de presentación, no de seguridad (mismo criterio que la bandeja).
 */
export function UsuariosScreen() {
  const { provider } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const canCreate = hasAnyPermission('users:manage', 'users:create_requester');
  const [personas, setPersonas] = useState<Requester[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState('');

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      const [p, c] = await Promise.all([listRequesters(token), listClientes(token)]);
      setPersonas(p);
      setClientes(c);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  const visibles = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return personas.filter((p) => {
      if (filtro === SIN_CLIENTE && p.clienteId) return false;
      if (filtro && filtro !== SIN_CLIENTE && p.clienteId !== filtro) return false;
      if (!needle) return true;
      return `${p.displayName ?? ''} ${p.email} ${p.clienteNombre ?? ''}`.toLowerCase().includes(needle);
    });
  }, [personas, q, filtro]);

  const sinCliente = personas.filter((p) => !p.clienteId).length;

  if (loading) return <div className={styles.loading}>Cargando personas…</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Usuarios</h2>
        {canCreate && (
          <button className={styles.createBtn} onClick={() => { setCreating(true); setNotice(null); }}>
            + Nueva persona
          </button>
        )}
      </div>

      {error && <div className={styles.error} style={{ margin: '0 0 12px' }}>{error}</div>}
      {notice && <div className={styles.success}>{notice}</div>}

      <div className={styles.toolbar}>
        <input
          className={styles.input}
          type="search"
          placeholder="Buscar por nombre, email o cliente"
          aria-label="Buscar personas"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className={styles.select} aria-label="Filtrar por cliente" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
          <option value="">Todos los clientes</option>
          {sinCliente > 0 && <option value={SIN_CLIENTE}>Sin cliente ({sinCliente})</option>}
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}{c.isActive ? '' : ' (desactivado)'}</option>
          ))}
        </select>
        <span className={styles.count}>{visibles.length} de {personas.length}</span>
      </div>

      <PersonasTabla personas={visibles} showCliente onChanged={load} onNotice={setNotice} />

      {creating && (
        <PersonaModal
          onClose={() => setCreating(false)}
          onSaved={async (p, { accessSent }) => {
            setCreating(false);
            setNotice(accessSent
              ? `${p.email} ya tiene acceso: le hemos enviado su enlace de entrada al portal.`
              : `${p.email} dado de alta sin enviarle el acceso. Mándaselo con «Enviar acceso» cuando toque.`);
            await load();
          }}
        />
      )}
    </div>
  );
}
